import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { images } from "../../data/content";

const ease = [0.16, 1, 0.3, 1];

const NationalCommitment = () => {
  const { isAr, t } = useLang();
  
  return (
    <section
      className="relative bg-bg-ivory py-24 md:py-36 overflow-hidden border-t border-brand-green/5"
      data-testid="national-section"
    >
      {/* Blueprint Grid Mesh Background */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(23,63,53,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(23,63,53,0.15) 1px, transparent 1px)`, 
          backgroundSize: "80px 80px" 
        }} 
      />

      {/* Decorative Blueprint Corner Marks */}
      <div className="absolute top-8 left-8 w-4 h-4 border-t border-l border-brand-green/15 pointer-events-none" />
      <div className="absolute top-8 right-8 w-4 h-4 border-t border-r border-brand-green/15 pointer-events-none" />
      <div className="absolute bottom-8 left-8 w-4 h-4 border-b border-l border-brand-green/15 pointer-events-none" />
      <div className="absolute bottom-8 right-8 w-4 h-4 border-b border-r border-brand-green/15 pointer-events-none" />

      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Top Mini Meta Header */}
        <div 
          className="flex justify-between items-center text-brand-green/40 mb-16 border-b border-brand-green/10 pb-6"
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs tracking-widest uppercase text-brand-green/60">
              [ {t("الالتزام الوطني", "NATIONAL VISION & COMMITMENT")} ]
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-mono text-xs tracking-widest hidden md:inline">KSA // VISION 2030</span>
            <span className="font-mono text-xs">06 — SEC</span>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-12 gap-x-8 gap-y-16 lg:gap-x-16 items-center">
          
          {/* Column 1: Image Showcase */}
          <motion.div 
            initial={{ opacity: 0, x: isAr ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
            className={`col-span-12 lg:col-span-6 xl:col-span-7 ${isAr ? "lg:order-2" : "lg:order-1"}`}
          >
            {/* Elegant double lined editorial frame */}
            <div className="relative border border-brand-green/10 p-3 bg-white/20 backdrop-blur-sm shadow-sm group">
              <div className="absolute inset-0 border border-brand-green/5 -m-2 pointer-events-none" />
              
              <div className="relative overflow-hidden h-[450px] md:h-[580px]">
                <img
                  src={images.saudiSkyline}
                  alt="Saudi Arabia Vision 2030"
                  className="w-full h-full object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-105"
                  style={{ filter: "contrast(1.02) saturate(0.92) brightness(0.98)" }}
                />
                
                {/* Soft luxury shadow overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-green/45 via-transparent to-transparent opacity-80 mix-blend-multiply pointer-events-none" />

                {/* Subtitle labels inside image */}
                <div className="absolute top-6 left-6 right-6 flex justify-between items-center text-white/90 z-20" dir={isAr ? "rtl" : "ltr"}>
                  <span className="font-mono text-[10px] tracking-widest uppercase bg-brand-green/60 backdrop-blur-md px-3 py-1 text-white border border-white/10">
                    — VISION 2030
                  </span>
                  <span className="font-mono text-[10px] tracking-widest uppercase bg-white/10 backdrop-blur-md px-3 py-1 text-white border border-white/5">
                    {t("المحتوى المحلي", "LOCAL CONTENT")}
                  </span>
                </div>

                <div className="absolute bottom-8 left-8 right-8 text-white z-20" dir={isAr ? "rtl" : "ltr"}>
                  <div className="font-mono text-xs uppercase tracking-widest text-brand-champagne/80 mb-2">
                    {t("المساهمة الوطنية", "National Contribution")}
                  </div>
                  <h4 className={`text-2xl md:text-3xl font-light ${isAr ? "font-arabic" : "font-serif italic"}`}>
                    {t("رؤية طموحة واقتصاد مستدام", "Ambitious Vision & Sustainable Economy")}
                  </h4>
                </div>
              </div>
            </div>

            {/* Subtle engineering notes below the photo */}
            <div 
              className="mt-6 flex justify-between text-[11px] font-mono text-brand-green/45 uppercase tracking-wider"
              dir={isAr ? "rtl" : "ltr"}
            >
              <span>SYS.REF: KSA-VISION-2030</span>
              <span>SCALE: 1.00 // LOCAL CONTENT</span>
            </div>
          </motion.div>

          {/* Column 2: Text Description & Custom Badges */}
          <motion.div 
            initial={{ opacity: 0, x: isAr ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease, delay: 0.15 }}
            className={`col-span-12 lg:col-span-6 xl:col-span-5 flex flex-col justify-center ${isAr ? "lg:order-1" : "lg:order-2"}`}
          >
            <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-green/50 mb-6 tracking-widester`} dir={isAr ? "rtl" : "ltr"}>
              {t("✦ الالتزام والتمكين", "✦ COMMITMENT & EMPOWERMENT")}
            </div>

            <h3
              className={`${isAr ? "font-arabic font-semibold" : "font-serif"} text-brand-green leading-[1.1] tracking-tight mb-10`}
              style={{ fontSize: isAr ? "clamp(36px, 4.5vw, 64px)" : "clamp(46px, 5vw, 82px)" }}
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr ? (
                <>
                  التزامنا الوطني <br />
                  <span className="font-light italic text-brand-green/85">ودعم رؤية المملكة ٢٠٣٠</span>
                </>
              ) : (
                <>
                  Our National <br />
                  <span className="font-light italic text-brand-green/80">Commitment & Vision</span>
                </>
              )}
            </h3>

            {/* Elegant description with vertical brand line */}
            <div 
              className={`relative ${isAr ? "pr-6 pl-0 border-r-2 border-brand-green/30" : "pl-6 pr-0 border-l-2 border-brand-green/30"} mb-12`}
              dir={isAr ? "rtl" : "ltr"}
            >
              <p 
                className={`text-brand-green/95 leading-relaxed text-[17px] md:text-[19px] font-light ${isAr ? "font-arabic text-right" : "font-sans text-left"}`}
                style={{ lineHeight: isAr ? 1.85 : 1.7 }}
              >
                {t(
                  "تدعم BigBrand أهداف رؤية المملكة ٢٠٣٠ عبر تعزيز المحتوى المحلي وتطوير القدرات الصناعية الوطنية، رافعةً تنافسية المنتجات المصنعة سعودياً في قطاعات الأبواب والحلول الخشبية وأنظمة WPC و UPVC بمواصفات عالمية.",
                  "BigBrand actively supports the strategic objectives of Saudi Vision 2030 by strengthening local content, advancing national industrial capabilities, and elevating the global competitiveness of Saudi-manufactured wood, WPC, and UPVC systems."
                )}
              </p>
            </div>

            {/* Premium structural chips instead of bullet points */}
            <div className="flex flex-col sm:flex-row gap-4 w-full" dir={isAr ? "rtl" : "ltr"}>
              <div 
                className="group flex-1 flex flex-col justify-between p-6 border border-brand-green/10 bg-white/20 backdrop-blur-sm hover:bg-brand-green hover:border-brand-green transition-all duration-500 cursor-pointer min-h-[140px]"
              >
                <div className="flex justify-between items-center text-brand-green/45 group-hover:text-brand-champagne/50 transition-colors font-mono text-xs">
                  <span>[ 01 ]</span>
                  <span>CONTENT</span>
                </div>
                <div className={`mt-6 text-brand-green group-hover:text-bg-ivory transition-colors font-semibold text-[17px] md:text-[18px] ${isAr ? "font-arabic" : "font-sans"}`}>
                  {t("المحتوى المحلي المعتمد", "Certified Local Content")}
                </div>
              </div>

              <div 
                className="group flex-1 flex flex-col justify-between p-6 border border-brand-green/10 bg-white/20 backdrop-blur-sm hover:bg-brand-green hover:border-brand-green transition-all duration-500 cursor-pointer min-h-[140px]"
              >
                <div className="flex justify-between items-center text-brand-green/45 group-hover:text-brand-champagne/50 transition-colors font-mono text-xs">
                  <span>[ 02 ]</span>
                  <span>VISION</span>
                </div>
                <div className={`mt-6 text-brand-green group-hover:text-bg-ivory transition-colors font-semibold text-[17px] md:text-[18px] ${isAr ? "font-arabic" : "font-sans"}`}>
                  {t("شريك رؤية ٢٠٣٠ الفاعل", "Active Vision 2030 Partner")}
                </div>
              </div>
            </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default NationalCommitment;
